export function authCallbackNeedsFreshSession({
  code,
  handoffSession,
  hasSession,
}: {
  code: string | null;
  handoffSession: { access_token: string; refresh_token: string } | null;
  hasSession: boolean;
}): boolean {
  if (handoffSession || code) return false;
  return !hasSession;
}

export function postAuthNextPath(next: string | null, origin: string): string {
  const url = new URL(safeNextPath(next), origin);
  url.searchParams.delete("login");
  return `${url.pathname}${url.search}${url.hash}`;
}

const POST_AUTH_NEXT_STORAGE_KEY = "arc-coach-post-auth-next";

export function authRedirectUrl({
  next,
  currentOrigin,
  siteUrl,
}: {
  next: string;
  currentOrigin: string;
  siteUrl?: string | null;
}): string {
  const origin = authRedirectOrigin({ currentOrigin, siteUrl });
  return `${origin}/auth/callback?next=${encodeURIComponent(safeNextPath(next))}`;
}

export function authProviderRedirectUrl({
  currentOrigin,
  siteUrl,
}: {
  currentOrigin: string;
  siteUrl?: string | null;
}): string {
  const origin = authRedirectOrigin({ currentOrigin, siteUrl });
  return `${origin}/auth/callback`;
}

export function rememberPostAuthNextPath(
  next: string,
  storage: Pick<Storage, "setItem"> = window.sessionStorage,
): void {
  try {
    storage.setItem(POST_AUTH_NEXT_STORAGE_KEY, safeNextPath(next));
  } catch {
    // Ignore storage issues and fall back to the callback query param.
  }
}

export function resolvePostAuthNextPath(
  next: string | null,
  storage: Pick<Storage, "getItem" | "removeItem"> = window.sessionStorage,
): string {
  if (next) {
    try {
      storage.removeItem(POST_AUTH_NEXT_STORAGE_KEY);
    } catch {
      // Ignore storage issues and honor the callback query param.
    }
    return safeNextPath(next);
  }

  try {
    const stored = storage.getItem(POST_AUTH_NEXT_STORAGE_KEY);
    storage.removeItem(POST_AUTH_NEXT_STORAGE_KEY);
    return safeNextPath(stored);
  } catch {
    return safeNextPath(next);
  }
}

function authRedirectOrigin({
  currentOrigin,
  siteUrl,
}: {
  currentOrigin: string;
  siteUrl?: string | null;
}): string {
  const current = safeOrigin(currentOrigin);
  const canonical = safeOrigin(siteUrl);
  if (!canonical) return current;

  const hostname = new URL(current).hostname.toLowerCase();
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith(".local")) {
    return current;
  }
  if (hostname.endsWith("pages.dev")) return current;
  return canonical;
}

function safeOrigin(value: string | null | undefined): string {
  if (!value?.trim()) return "";
  try {
    return new URL(value).origin;
  } catch {
    return "";
  }
}

export async function waitForAuthSession({
  readSession,
  attempts = 4,
  waitMs = 75,
  sleep = defaultSleep,
}: {
  readSession: () => Promise<boolean>;
  attempts?: number;
  waitMs?: number;
  sleep?: (ms: number) => Promise<void>;
}): Promise<boolean> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (await readSession()) return true;
    if (attempt < attempts - 1) await sleep(waitMs);
  }
  return false;
}

export async function waitForAuthResolution({
  waitForSession,
  waitForEventSession,
}: {
  waitForSession: () => Promise<boolean>;
  waitForEventSession: () => Promise<boolean>;
}): Promise<boolean> {
  const [eventResult, sessionResult] = await Promise.all([
    waitForEventSession(),
    waitForSession(),
  ]);
  return eventResult || sessionResult;
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function safeNextPath(next: string | null): string {
  if (!next || !next.startsWith("/") || next.startsWith("//")) return "/today";
  return next;
}
